import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Copy, Linkedin, Twitter } from "lucide-react";
import { toast } from "sonner";

import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { ArticleBody } from "@/components/marketing/ArticleBody";
import { ArticleCard } from "@/components/marketing/ArticleCard";

import {
  absoluteUrl,
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
  buildOrganizationJsonLd,
} from "@/lib/seo";
import {
  useMarketingArticleBySlug,
  useRelatedArticles,
} from "@/hooks/useMarketingArticles";
import { RESOURCE_CATEGORIES } from "@/lib/marketing-categories";
import { useLocale, intlLocale, pickDbText } from "@/lib/locale";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

function buildToc(markdown: string): TocItem[] {
  const items: TocItem[] = [];
  const re = /^(#{2,3})\s+(.+?)\s*$/gm;
  let m: RegExpExecArray | null;
  while ((m = re.exec(markdown))) {
    const level = m[1].length;
    const text = m[2].trim();
    const id = text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    items.push({ id, text, level });
  }
  return items;
}

export default function ResourceArticlePage() {
  // The matched route is /ressources/:slugOrCategory (ResourcesParamDispatcher
  // decides between this page and ResourcesCategoryPage), so the route param
  // key is slugOrCategory, not slug -- reading `slug` here always returned
  // undefined, which disabled the article query and silently redirected every
  // resource-article URL to the hub. See ResourcesParamDispatcher in App.tsx.
  const { slugOrCategory: slug } = useParams<{ slugOrCategory: string }>();
  const { t, i18n } = useTranslation();
  const uiLocale = useLocale();
  const { data: article, isLoading } = useMarketingArticleBySlug(slug);
  const { data: related = [] } = useRelatedArticles(slug, article?.related_slugs ?? [], article?.category);
  const [activeId, setActiveId] = useState<string | null>(null);

  const body = useMemo(() => {
    if (!article) return "";
    return pickDbText(article, "body", uiLocale);
  }, [article, uiLocale]);
  const toc = useMemo(() => buildToc(body), [body]);

  useEffect(() => {
    if (toc.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActiveId((e.target as HTMLElement).id);
        }
      },
      { rootMargin: "-30% 0px -55% 0px" },
    );
    toc.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [toc, body]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-24">
        <div className="h-6 w-40 animate-pulse bg-muted" />
        <div className="mt-6 h-12 w-3/4 animate-pulse bg-muted" />
      </div>
    );
  }
  if (!article) return <Navigate to="/ressources" replace />;

  const title = pickDbText(article, "title", uiLocale);
  const excerpt = pickDbText(article, "excerpt", uiLocale);
  const metaTitle =
    pickDbText(article, "meta_title", uiLocale) || title;
  const metaDescription =
    pickDbText(article, "meta_description", uiLocale) || excerpt;
  const category = RESOURCE_CATEGORIES.find((c) => c.slug === article.category);
  const date = new Intl.DateTimeFormat(intlLocale(uiLocale), {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(article.published_at));

  const articleJsonLd = buildArticleJsonLd({
    title: metaTitle,
    description: metaDescription,
    path: `/ressources/${article.slug}`,
    locale: uiLocale,
    image: article.hero_image_url ?? undefined,
    publishedAt: article.published_at,
    modifiedAt: article.updated_at,
    authorName: article.author_name,
    section: category ? t(`marketing.resources.categories.${category.i18n}.label`) : undefined,
    tags: article.primary_keyword ? [article.primary_keyword] : undefined,
  });

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: t("marketing.breadcrumbs.home"), path: "/" },
    { name: t("marketing.header.nav.resources"), path: "/ressources" },
    ...(category
      ? [{ name: t(`marketing.resources.categories.${category.i18n}.label`), path: `/ressources/${category.slug}` }]
      : []),
    { name: title, path: `/ressources/${article.slug}` },
  ]);

  const canonical = article.canonical_url ?? absoluteUrl(`/ressources/${article.slug}`);
  const articleUrl = absoluteUrl(`/ressources/${article.slug}`);
  const shareX = `https://x.com/intent/post?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(title)}`;
  const shareLi = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(articleUrl);
      toast.success(t("marketing.resources.article.copied"));
    } catch {
      toast.error(t("marketing.resources.article.copyError"));
    }
  }

  return (
    <>
      <SeoHead
        title={metaTitle}
        description={metaDescription}
        path={`/ressources/${article.slug}`}
        locale={uiLocale}
        image={article.hero_image_url ?? undefined}
        type="article"
        jsonLd={[buildOrganizationJsonLd(), articleJsonLd, breadcrumbJsonLd]}
      />
      {/* canonical override is emitted by SeoHead; keep this string available for tests / SSR audit */}
      <span className="sr-only" data-canonical={canonical} />

      <Section padding="md">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li><Link to="/" className="hover:text-foreground">{t("marketing.breadcrumbs.home")}</Link></li>
            <li aria-hidden>·</li>
            <li><Link to="/ressources" className="hover:text-foreground">{t("marketing.header.nav.resources")}</Link></li>
            {category && (
              <>
                <li aria-hidden>·</li>
                <li>
                  <Link to={`/ressources/${category.slug}`} className="hover:text-foreground">
                    {t(`marketing.resources.categories.${category.i18n}.label`)}
                  </Link>
                </li>
              </>
            )}
          </ol>
        </nav>

        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-[1fr_280px]">
          <div className="min-w-0">
            <Eyebrow>
              {category ? t(`marketing.resources.categories.${category.i18n}.label`) : ""}
            </Eyebrow>
            <DisplayHeading level={1} className="mt-4">{title}</DisplayHeading>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{excerpt}</p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span>{t("marketing.resources.article.byline", { author: article.author_name })}</span>
              <span aria-hidden className="text-rule">·</span>
              <span>{date}</span>
              <span aria-hidden className="text-rule">·</span>
              <span>{t("marketing.resources.readingTime", { min: article.reading_minutes })}</span>
            </div>

            {article.hero_image_url && (
              <figure className="mt-10">
                <img
                  src={article.hero_image_url}
                  alt=""
                  width={1200}
                  height={630}
                  loading="eager"
                  fetchPriority="high"
                  className="aspect-[16/9] w-full border border-rule object-cover"
                />
                {article.hero_image_credit && (
                  <figcaption className="dateline mt-2">{article.hero_image_credit}</figcaption>
                )}
              </figure>
            )}

            <Rule className="my-10" />

            <div className="max-w-[680px]">
              <ArticleBody markdown={body} />
            </div>

            <Rule className="my-12" />

            <aside className="border border-rule bg-paper p-6">
              <p className="dateline">{t("marketing.resources.article.authorEyebrow")}</p>
              <p className="mt-3 text-display text-lg">{article.author_name}</p>
              <p className="mt-2 text-sm text-muted-foreground">{t("marketing.resources.article.authorBio")}</p>
            </aside>

            {related.length > 0 && (
              <div className="mt-12">
                <h2 className="text-display text-2xl">{t("marketing.resources.article.related")}</h2>
                <div className="mt-6 grid grid-cols-1 gap-px bg-rule md:grid-cols-3">
                  {related.map((r) => (
                    <ArticleCard key={r.id} article={r} />
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-rule pt-8">
              <Link to="/ressources" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> {t("marketing.resources.article.backToHub")}
              </Link>
              <Link to="/contact" className="inline-flex h-11 items-center gap-2 bg-accent-press px-5 text-xs font-semibold uppercase tracking-wider text-accent-press-foreground">
                {t("marketing.resources.article.talkToExpert")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-32 flex flex-col gap-8">
              {toc.length > 0 && (
                <nav>
                  <p className="dateline mb-3">{t("marketing.resources.article.tableOfContents")}</p>
                  <ol className="space-y-2 border-l border-rule pl-4">
                    {toc.map((it) => (
                      <li key={it.id} className={it.level === 3 ? "pl-3" : ""}>
                        <a
                          href={`#${it.id}`}
                          className={
                            (activeId === it.id ? "text-accent-press font-medium " : "text-muted-foreground hover:text-foreground ") +
                            "text-sm transition-colors"
                          }
                        >
                          {it.text}
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>
              )}

              <div>
                <p className="dateline mb-3">{t("marketing.resources.article.share")}</p>
                <div className="flex items-center gap-2">
                  <a href={shareX} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className="flex h-9 w-9 items-center justify-center border border-rule text-muted-foreground hover:text-foreground">
                    <Twitter className="h-4 w-4" />
                  </a>
                  <a href={shareLi} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="flex h-9 w-9 items-center justify-center border border-rule text-muted-foreground hover:text-foreground">
                    <Linkedin className="h-4 w-4" />
                  </a>
                  <button onClick={copyLink} aria-label={t("marketing.resources.article.copyLink")} className="flex h-9 w-9 items-center justify-center border border-rule text-muted-foreground hover:text-foreground">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </Section>
    </>
  );
}
